import React, { useEffect, useState } from "react";
import api, { money, formatApiError } from "../lib/api";
import PageHeader from "../components/app/PageHeader";
import ImportRulesModal from "../components/app/ImportRulesModal";
import { Plus, Search, Edit, Trash2, X, Upload, Database } from "lucide-react";

const CATEGORIES = [
  "Vehicle Signage","Building Signage","Window Frosting","Translucent Vinyl",
  "ACM Panels","Banners","Corflutes","Promotional Products",
  "Machine Hire","Design","Installation","Production","Removal","Other",
];

export default function Pricing() {
  const [rules, setRules] = useState([]);
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("");
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);

  const load = async () => {
    const { data } = await api.get("/pricing-rules", { params: { q: q || undefined, category: cat || undefined } });
    setRules(data);
  };
  useEffect(() => { const t = setTimeout(load, 200); return () => clearTimeout(t); /* eslint-disable-next-line */ }, [q, cat]);

  const remove = async (r) => {
    if (!confirm(`Remove "${r.item_name}"?`)) return;
    await api.delete(`/pricing-rules/${r.id}`);
    load();
  };

  return (
    <div data-testid="pricing-page">
      <PageHeader
        eyebrow="Library"
        title="Pricing Rules"
        subtitle="The items, hire, and labour that power your quotes."
        actions={
          <div className="flex items-center gap-2">
            <button onClick={() => setImportOpen(true)} data-testid="pricing-import-btn" className="btn btn-secondary">
              <Upload className="w-4 h-4" /> Import CSV / XLSX
            </button>
            <button onClick={() => { setEditing(null); setOpen(true); }} data-testid="pricing-new-btn" className="btn btn-primary">
              <Plus className="w-4 h-4" /> New Rule
            </button>
          </div>
        }
      />

      <div className="p-10">
        <div className="flex items-center gap-3 mb-6">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-3 w-4 h-4" style={{ color: "var(--text-faint)" }} />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search items, suppliers…" data-testid="pricing-search-input" className="input pl-10" />
          </div>
          <select value={cat} onChange={(e) => setCat(e.target.value)} data-testid="pricing-category-filter" className="input" style={{ width: "auto", minWidth: 200 }}>
            <option value="">All categories</option>
            {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
          <div className="ml-auto label-eyebrow">{rules.length} items</div>
        </div>

        <div className="surface overflow-hidden">
          <table className="data-table">
            <thead>
              <tr>
                <th>Item</th>
                <th>Category</th>
                <th>Unit</th>
                <th className="text-right">Cost</th>
                <th className="text-right">Sell</th>
                <th className="text-right">Markup</th>
                <th>Supplier</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {rules.length === 0 && (
                <tr><td colSpan="8" className="text-center py-20">
                  <div className="flex flex-col items-center gap-3">
                    <Database className="w-8 h-8" style={{ color: "var(--text-faint)" }} />
                    <div className="text-sm" style={{ color: "var(--text-muted)" }}>
                      No rules match. Try clearing filters or import a price list.
                    </div>
                  </div>
                </td></tr>
              )}
              {rules.map((r) => {
                const markup = r.cost > 0 ? ((r.sell - r.cost) / r.cost) * 100 : 0;
                return (
                  <tr key={r.id} data-testid={`rule-row-${r.id}`}>
                    <td>
                      <div className="font-medium">{r.item_name}</div>
                      {r.description && <div className="text-xs mt-0.5" style={{ color: "var(--text-faint)" }}>{r.description}</div>}
                    </td>
                    <td className="text-xs font-mono" style={{ color: "var(--text-muted)" }}>{r.category}</td>
                    <td className="text-xs font-mono">{r.unit}</td>
                    <td className="text-right font-mono" data-num>{money(r.cost)}</td>
                    <td className="text-right font-mono" data-num>{money(r.sell)}</td>
                    <td
                      className="text-right font-mono"
                      style={{ color: markup >= 100 ? "var(--success)" : "var(--text-secondary)" }}
                      data-num
                    >
                      {markup.toFixed(0)}%
                    </td>
                    <td className="text-xs" style={{ color: "var(--text-muted)" }}>{r.supplier || "—"}</td>
                    <td className="text-right whitespace-nowrap">
                      <button onClick={() => { setEditing(r); setOpen(true); }} className="btn btn-ghost btn-icon" data-testid={`rule-edit-${r.id}`}>
                        <Edit className="w-3.5 h-3.5" />
                      </button>
                      <button onClick={() => remove(r)} className="btn btn-ghost btn-icon" data-testid={`rule-delete-${r.id}`}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {open && <RuleForm editing={editing} onClose={() => setOpen(false)} onSaved={() => { setOpen(false); load(); }} />}
      {importOpen && <ImportRulesModal onClose={() => setImportOpen(false)} onDone={load} />}
    </div>
  );
}

function Modal({ children, onClose, maxWidth = "xl" }) {
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-6"
      style={{ background: "rgba(7, 9, 26, 0.75)", backdropFilter: "blur(6px)" }}
      onClick={onClose}
    >
      <div
        className="surface w-full max-h-[88vh] flex flex-col"
        style={{ maxWidth: maxWidth === "xl" ? 640 : 800, background: "var(--surface-1)" }}
        onClick={(e) => e.stopPropagation()}
      >
        {children}
      </div>
    </div>
  );
}

function RuleForm({ editing, onClose, onSaved }) {
  const [form, setForm] = useState(
    editing || {
      category: "Vehicle Signage", item_name: "", description: "", unit: "ea",
      cost: 0, sell: 0, default_labour_hours: 0, default_wastage_pct: 0, supplier: "",
    }
  );
  const [saving, setSaving] = useState(false);

  const markup = form.cost > 0 ? ((form.sell - form.cost) / form.cost) * 100 : 0;

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        ...form,
        cost: parseFloat(form.cost) || 0,
        sell: parseFloat(form.sell) || 0,
        default_labour_hours: parseFloat(form.default_labour_hours) || 0,
        default_wastage_pct: parseFloat(form.default_wastage_pct) || 0,
      };
      if (editing?.id) await api.put(`/pricing-rules/${editing.id}`, payload);
      else await api.post("/pricing-rules", payload);
      onSaved();
    } catch (e) { alert(formatApiError(e)); }
    finally { setSaving(false); }
  };

  return (
    <Modal onClose={onClose}>
      <div className="px-6 py-5 border-b flex items-center justify-between" style={{ borderColor: "var(--border-subtle)" }}>
        <div>
          <div className="eyebrow-accent mb-1">Library</div>
          <h2 className="font-display text-xl font-bold">{editing ? "Edit Pricing Rule" : "New Pricing Rule"}</h2>
        </div>
        <button onClick={onClose} className="btn btn-ghost btn-icon"><X /></button>
      </div>

      <div className="p-6 space-y-4 overflow-auto">
        <div className="grid grid-cols-3 gap-3">
          <div>
            <label className="label-eyebrow block mb-1.5">Category</label>
            <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="input">
              {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <div className="col-span-2">
            <label className="label-eyebrow block mb-1.5">Item Name</label>
            <input value={form.item_name} onChange={(e) => setForm({ ...form, item_name: e.target.value })} data-testid="rule-name-input" className="input" />
          </div>
        </div>
        <div>
          <label className="label-eyebrow block mb-1.5">Description</label>
          <input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="input" placeholder="Optional notes" />
        </div>
        <div className="grid grid-cols-3 gap-3">
          <div>
            <label className="label-eyebrow block mb-1.5">Unit</label>
            <input value={form.unit} onChange={(e) => setForm({ ...form, unit: e.target.value })} className="input" />
          </div>
          <div>
            <label className="label-eyebrow block mb-1.5">Cost</label>
            <input type="number" step="0.01" value={form.cost} onChange={(e) => setForm({ ...form, cost: e.target.value })} data-testid="rule-cost-input" className="input input-mono text-right" />
          </div>
          <div>
            <label className="label-eyebrow block mb-1.5">Sell</label>
            <input type="number" step="0.01" value={form.sell} onChange={(e) => setForm({ ...form, sell: e.target.value })} data-testid="rule-sell-input" className="input input-mono text-right" />
          </div>
        </div>
        <div className="grid grid-cols-3 gap-3">
          <div>
            <label className="label-eyebrow block mb-1.5">Labour hrs (default)</label>
            <input type="number" step="0.25" value={form.default_labour_hours} onChange={(e) => setForm({ ...form, default_labour_hours: e.target.value })} className="input input-mono text-right" />
          </div>
          <div>
            <label className="label-eyebrow block mb-1.5">Wastage % (default)</label>
            <input type="number" step="1" value={form.default_wastage_pct} onChange={(e) => setForm({ ...form, default_wastage_pct: e.target.value })} className="input input-mono text-right" />
          </div>
          <div>
            <label className="label-eyebrow block mb-1.5">Supplier</label>
            <input value={form.supplier} onChange={(e) => setForm({ ...form, supplier: e.target.value })} className="input" />
          </div>
        </div>
        <div className="surface-inset p-3 flex items-center justify-between text-sm">
          <span className="label-eyebrow">Computed markup</span>
          <span
            className="font-mono font-bold text-lg"
            style={{ color: markup >= 100 ? "var(--success)" : "var(--text-primary)" }}
            data-num
          >
            {markup.toFixed(0)}%
          </span>
        </div>
      </div>

      <div className="px-6 py-4 border-t flex justify-end gap-2" style={{ borderColor: "var(--border-subtle)" }}>
        <button onClick={onClose} className="btn btn-ghost">Cancel</button>
        <button onClick={save} disabled={saving || !form.item_name} data-testid="rule-save-btn" className="btn btn-primary">
          {saving ? "Saving…" : "Save Rule"}
        </button>
      </div>
    </Modal>
  );
}
